import { useState } from "react"

import { ContentObjectItem, ExecutionRun, InteractionRef } from "@vertesia/common"
import { Loader, Send } from "lucide-react"

import { ServiceStreamingDonePanel } from "@components/panels/services/ServiceStreamingDonePanel"
import { ServiceStreamingPanel } from "@components/panels/services/ServiceStreamingPanel"

import { FormExecutionStatus } from "@plugins/form/FormState"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/select"

interface ChatPanelProps {
    document: ContentObjectItem
    interactions: InteractionRef[]
}
export function ServicesPanel({ document, interactions }: Readonly<ChatPanelProps>) {
    const { client } = useUserSession()

    const [run, setRun] = useState<ExecutionRun | undefined>(undefined)
    const [status, setStatus] = useState<FormExecutionStatus>(FormExecutionStatus.pending)
    const [service, setService] = useState<string | undefined>()
    const [content, setContent] = useState<string>("")

    const onCallService = async () => {
        if (!service) {
            return
        }

        const result = await client.runs.create({
            interaction: service,
            data: {
                document: `store:${document.id}`,
            },
            tags: ["app-demo-template"],
            stream: true,
        })
        setStatus(FormExecutionStatus.streaming)
        setRun(result as ExecutionRun)
    }

    const handleSetService = (value: string) => {
        setContent("")
        setService(value)
    }

    const onStreamingDone = (value: string) => {
        setContent(value)
        setStatus(FormExecutionStatus.done)
        setRun(undefined)
    }

    return (
        <div className="flex flex-col gap-2 p-2 h-full bg-gray-100">
            <div className="flex w-full gap-2">
                <Select onValueChange={handleSetService} disabled={status === FormExecutionStatus.streaming}>
                    <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select Service" />
                    </SelectTrigger>
                    <SelectContent>
                        {interactions.map((interaction) => (
                            <SelectItem key={interaction.id} value={interaction.id}>
                                {interaction.name} - V{interaction.version}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button variant="outline" onClick={onCallService} disabled={status === FormExecutionStatus.streaming || !service}>
                    {status === FormExecutionStatus.streaming ? <Loader className="h-16 w-16 animate-spin motion-safe:animate-[spin_3s_linear_infinite]" /> : <Send />}
                </Button>
            </div>
            {status !== FormExecutionStatus.streaming && status !== FormExecutionStatus.done && <div className="text-gray-500 text-center">Run the analysis to see the result here</div>}
            {status === FormExecutionStatus.streaming && run?.id && <ServiceStreamingPanel run={run} onStreamingDone={onStreamingDone} />}
            {status === FormExecutionStatus.done && <ServiceStreamingDonePanel content={content} />}
        </div>
    )
}
